import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Plane, Route as RouteIcon, Radar, Globe2, Hash, MapPin } from "lucide-react";
import { getRecent, RecentItem } from "@/lib/recent";
import { useSEO } from "@/lib/seo";
import { faqJsonLd } from "@/lib/jsonld";
import { useTranslation, Trans } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";
import { alternateUrls } from "@/i18n/paths";

import { AffiliateTile } from "@/components/AffiliateTile";
import { useRandomAffiliate } from "@/hooks/useRandomAffiliate";
import { FlightSearchForm } from "@/components/FlightSearchForm";
import { cn } from "@/lib/utils";

const POPULAR = [
  { code: "ARN", city: "Stockholm" },
  { code: "GOT", city: "Göteborg" },
  { code: "MMX", city: "Malmö" },
  { code: "CPH", city: "Köpenhamn" },
  { code: "OSL", city: "Oslo" },
  { code: "LHR", city: "London" },
  { code: "CDG", city: "Paris" },
  { code: "JFK", city: "New York" },
];

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation(["home", "seo"]);
  const { lang, href } = useLang();
  const [recent, setRecent] = useState<RecentItem[]>([]);

  useEffect(() => setRecent(getRecent()), []);
  const ad = useRandomAffiliate("home");

  const alts = alternateUrls("/");
  useSEO({
    title: t("seo:home.title"),
    description: t("seo:home.description"),
    keywords: t("seo:home.keywords"),
    canonical: lang === "en" ? alts.en : alts.sv,
    alternates: { sv: alts.sv, en: alts.en, "x-default": alts.sv },
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Flyg.info",
        url: "https://flyg.info/",
        applicationCategory: "TravelApplication",
        operatingSystem: "Web",
        inLanguage: lang === "en" ? "en" : "sv-SE",
        offers: { "@type": "Offer", price: "0", priceCurrency: "SEK" },
      },
      faqJsonLd((t("home:faq", { returnObjects: true }) as { q: string; a: string }[]) || []),
    ],
  });

  return (
    <div className="bg-hero">
      <section className="px-4 sm:px-6 md:px-10 pt-12 md:pt-24 pb-10 md:pb-16 max-w-4xl mx-auto">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
          {t("home:eyebrow")}
        </div>
        <h1 className="font-display text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.5rem] font-medium tracking-tight leading-[1.02]">
          {t("home:title1")}
          <br />
          <span className="italic text-muted-foreground">{t("home:title2")}</span>
        </h1>
        <p className="mt-5 text-sm sm:text-base md:text-lg text-muted-foreground max-w-xl">
          <Trans
            i18nKey="home:subtitle"
            values={{ flightExample: "SK1234", routeExample: "ARN-LHR" }}
            components={{ 1: <span className="font-mono" /> }}
          />
        </p>

        <FlightSearchForm autoFocus className="mt-7" />

        {recent.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
              <Clock className="h-3 w-3" /> {t("home:recent")}
            </div>
            <div className="flex flex-wrap gap-2">
              {recent.slice(0, 6).map((r, i) => (
                <button
                  key={i}
                  onClick={() => navigate(href(r.type === "flight" ? `/flight/${r.value}` : `/route/${r.from}-${r.to}`))}
                  className="inline-flex items-center gap-1.5 rounded-full border border-rule bg-card hover:border-foreground/30 px-3 py-1 text-xs font-mono transition-colors"
                >
                  {r.type === "flight" ? <Plane className="h-3 w-3" /> : <RouteIcon className="h-3 w-3" />}
                  {r.type === "flight" ? r.value : `${r.from} → ${r.to}`}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="px-4 sm:px-6 md:px-10 pt-2 pb-8 max-w-4xl mx-auto">
        <AffiliateTile affiliate={ad} variant="prominent" />
      </section>

      <section className="px-4 sm:px-6 md:px-10 py-8 max-w-4xl mx-auto border-t border-rule">
        <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">
          {t("home:h2Live")}
        </h2>
        <div className="mt-5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
          {t("home:quickLinksTitle")}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <button
            onClick={() => navigate(href("/flygradar/sverige"))}
            className="flex items-center gap-2 rounded-md border border-rule bg-card hover:border-foreground/30 px-3 py-2.5 text-xs sm:text-sm transition-colors text-left"
          >
            <Radar className="h-3.5 w-3.5 shrink-0" />
            <span className="leading-tight">{t("home:quickLinks.sweden")}</span>
          </button>
          <button
            onClick={() => navigate(href("/flygradar/europa"))}
            className="flex items-center gap-2 rounded-md border border-rule bg-card hover:border-foreground/30 px-3 py-2.5 text-xs sm:text-sm transition-colors text-left"
          >
            <Globe2 className="h-3.5 w-3.5 shrink-0" />
            <span className="leading-tight">{t("home:quickLinks.europe")}</span>
          </button>
          <button
            onClick={() => navigate(href("/live"))}
            className="flex items-center gap-2 rounded-md border border-rule bg-card hover:border-foreground/30 px-3 py-2.5 text-xs sm:text-sm transition-colors text-left"
          >
            <Plane className="h-3.5 w-3.5 shrink-0" />
            <span className="leading-tight">{t("home:quickLinks.live")}</span>
          </button>
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              setTimeout(() => {
                document.querySelector<HTMLInputElement>("form input")?.focus();
              }, 300);
            }}
            className="flex items-center gap-2 rounded-md border border-rule bg-card hover:border-foreground/30 px-3 py-2.5 text-xs sm:text-sm transition-colors text-left"
          >
            <Hash className="h-3.5 w-3.5 shrink-0" />
            <span className="leading-tight">{t("home:quickLinks.flightNumber")}</span>
          </button>
          <button
            onClick={() => navigate(href("/live"))}
            className="flex items-center gap-2 rounded-md border border-rule bg-card hover:border-foreground/30 px-3 py-2.5 text-xs sm:text-sm transition-colors text-left"
          >
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="leading-tight">{t("home:quickLinks.overhead")}</span>
          </button>
        </div>
      </section>

      <section className="px-4 sm:px-6 md:px-10 py-10 md:py-12 max-w-4xl mx-auto border-t border-rule">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">{t("home:popular")}</h2>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hidden sm:inline">{t("home:popularLive")}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-rule border border-rule rounded-md overflow-hidden">
          {POPULAR.map((a) => (
            <button
              key={a.code}
              onClick={() => navigate(href(`/airport/${a.code}`))}
              className={cn(
                "group bg-card hover:bg-surface-2 text-left p-4 transition-colors"
              )}
            >
              <div className="font-mono text-2xl font-semibold tracking-tight">{a.code}</div>
              <div className="text-xs text-muted-foreground mt-1">{a.city}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="px-4 sm:px-6 md:px-10 py-10 md:py-14 max-w-4xl mx-auto border-t border-rule">
        <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">
          {t("home:seoSection.title")}
        </h2>
        <div className="mt-5 grid sm:grid-cols-2 gap-6 text-sm sm:text-[15px] leading-relaxed text-muted-foreground">
          <p>
            <Trans i18nKey="home:seoSection.p1" components={{ strong: <strong className="text-foreground" />, code: <span className="font-mono" /> }} />
          </p>
          <p>
            <Trans i18nKey="home:seoSection.p2" components={{ strong: <strong className="text-foreground" /> }} />
          </p>
          <p>
            <Trans i18nKey="home:seoSection.p3" />
          </p>
          <p>
            <Trans
              i18nKey="home:seoSection.p4"
              components={{ a: <a href={href("/spel")} className="text-foreground underline underline-offset-4" /> }}
            />
          </p>
          <p>
            <Trans i18nKey="home:seoSection.p5" components={{ strong: <strong className="text-foreground" /> }} />
          </p>
          <p>
            <Trans i18nKey="home:seoSection.p6" components={{ strong: <strong className="text-foreground" /> }} />
          </p>
        </div>
      </section>
    </div>
  );
}